/* eslint-disable react/jsx-props-no-spreading */
import { useUser } from '@folklore/auth';
import { useForm } from '@folklore/forms';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import useSetUser from '../../hooks/useSetUser';
import * as AppPropTypes from '../../lib/PropTypes';

import { useThemeClassName } from '../../contexts/ThemeContext';
import SubmitButton from '../buttons/SubmitButton';
import DateField from '../fields/Date';
import EmailField from '../fields/Email';
import FieldLabel from '../fields/FieldLabel';
import PhoneField from '../fields/Phone';
import ProvinceField from '../fields/Province';
import SelectField from '../fields/Select';
import TextField from '../fields/Text';

import styles from '../../../styles/forms/prize.module.scss';

const propTypes = {
    prize: AppPropTypes.prize,
    className: PropTypes.string,
    selectPlaceholderClassName: PropTypes.string,
    onSuccess: PropTypes.func,
};

const defaultProps = {
    prize: null,
    className: null,
    selectPlaceholderClassName: null,
    onSuccess: null,
};

// TODO: refactor this with field rows

function PrizeForm({ prize, className, selectPlaceholderClassName, onSuccess }) {
    const intl = useIntl();
    const themeClassName = useThemeClassName(styles);
    const route = useUrlGenerator();
    const {
        uuid,
        fields: prizeFields = null,
        items: prizeItems = null,
        prizeItem = null,
        prize: prizeObj = null,
    } = prize || {};
    const { type: prizeType = null } = prizeObj || {};
    const { id: prizeItemId = null } = prizeItem || {};
    const action = route('prize.claim', { participant_prize: uuid });
    const user = useUser();
    const setUser = useSetUser();

    const onComplete = useCallback(
        (newUser) => {
            setUser(newUser);
            if (onSuccess !== null) {
                onSuccess(newUser);
            }
        },
        [setUser, onSuccess],
    );

    const fieldsNames = useMemo(
        () => [
            ...[
                'first_name',
                'last_name',
                'phone',
                'email',
                'address',
                'city',
                'postal_code',
                'region',
                'math',
            ],
            ...(prizeFields || []).map(({ name }) => name),
            ...(prizeItems !== null ? ['prize_item_id'] : []),
        ],
        [prizeFields, prizeItems],
    );

    const prizeItemsOptions = useMemo(
        () =>
            prizeItems !== null
                ? prizeItems.map(({ id, type, date }) => {
                      let label = intl.formatMessage(
                          {
                              defaultMessage: 'Item #{id}',
                              description: 'Prize item label',
                          },
                          {
                              id,
                          },
                      );
                      if (type === 'ticket' && date !== null) {
                          label = intl.formatMessage(
                              {
                                  defaultMessage: 'Ticket for {date}',
                                  description: 'Ticket date label',
                              },
                              {
                                  date: intl.formatDate(parseISO(date), {
                                      day: '2-digit',
                                      weekday: 'short',
                                      month: 'long',
                                      year: 'numeric',
                                  }),
                              },
                          );
                      }
                      return {
                          id,
                          label,
                      };
                  })
                : null,
        [prizeItems, intl],
    );

    const initialFieldsValue = useMemo(() => {
        const { firstName, lastName, phone, email, address, city, region, postalCode } = user || {};
        return {
            first_name: firstName,
            last_name: lastName,
            phone,
            email,
            address,
            city,
            postal_code: postalCode,
            region,
            prize_item_id: prizeItemId,
        };
    }, [user, prizeItemId]);

    const {
        fields,
        onSubmit,
        errors,
        loading,
        generalError = null,
    } = useForm({
        fields: fieldsNames,
        initialValue: initialFieldsValue,
        action,
        onComplete,
    });

    const {
        first_name: firstNameErrors = null,
        last_name: lastNameErrors = null,
        email: emailErrors = null,
        phone: phoneErrors = null,
        address: addressErrors = null,
        city: cityErrors = null,
        postal_code: postalCodeErrors = null,
        math: mathErrors = null,
        region: regionErrors = null,
        prize_item_id: prizeItemIdErrors = null,
    } = errors || {};

    return (
        <form
            className={classNames([
                styles.container,
                {
                    [styles.loading]: loading,
                },
                themeClassName,
                className,
            ])}
            onSubmit={onSubmit}
            action={action}
        >
            <div className={styles.inner}>
                <div
                    className={classNames([
                        styles.formGroup,
                        {
                            [styles.hasError]: firstNameErrors !== null,
                        },
                    ])}
                >
                    <FieldLabel className={styles.label}>
                        <FormattedMessage defaultMessage="First name" description="Field label" />
                    </FieldLabel>
                    <TextField
                        className={styles.input}
                        {...fields.first_name}
                        placeholder={intl.formatMessage({
                            defaultMessage: 'First name',
                            description: 'Field placeholder',
                        })}
                    />
                    {firstNameErrors !== null ? (
                        <div className={styles.errors}>{firstNameErrors.join(<br />)}</div>
                    ) : null}
                </div>
                <div
                    className={classNames([
                        styles.formGroup,
                        {
                            [styles.hasError]: lastNameErrors !== null,
                        },
                    ])}
                >
                    <FieldLabel className={styles.label}>
                        <FormattedMessage defaultMessage="Last name" description="Field label" />
                    </FieldLabel>
                    <TextField
                        className={styles.input}
                        {...fields.last_name}
                        placeholder={intl.formatMessage({
                            defaultMessage: 'Last name',
                            description: 'Field placeholder',
                        })}
                    />
                    {lastNameErrors !== null ? (
                        <div className={styles.errors}>{lastNameErrors.join(<br />)}</div>
                    ) : null}
                </div>
                <div
                    className={classNames([
                        styles.formGroup,
                        {
                            [styles.hasError]: phoneErrors !== null,
                        },
                    ])}
                >
                    <FieldLabel className={styles.label}>
                        <FormattedMessage defaultMessage="Phone number" description="Field label" />
                    </FieldLabel>
                    <PhoneField
                        className={styles.input}
                        {...fields.phone}
                        placeholder={intl.formatMessage({
                            defaultMessage: 'Phone number',
                            description: 'Field placeholder',
                        })}
                    />
                    {phoneErrors !== null ? (
                        <div className={styles.errors}>{phoneErrors.join(<br />)}</div>
                    ) : null}
                </div>
                <div
                    className={classNames([
                        styles.formGroup,
                        {
                            [styles.hasError]: emailErrors !== null,
                        },
                    ])}
                >
                    <FieldLabel className={styles.label}>
                        <FormattedMessage defaultMessage="Email" description="Field label" />
                    </FieldLabel>
                    <EmailField
                        className={styles.input}
                        {...fields.email}
                        placeholder={intl.formatMessage({
                            defaultMessage: 'Email',
                            description: 'Field placeholder',
                        })}
                    />
                    {emailErrors !== null ? (
                        <div className={styles.errors}>{emailErrors.join(<br />)}</div>
                    ) : null}
                </div>

                <div
                    className={classNames([
                        styles.formGroup,
                        {
                            [styles.hasError]: addressErrors !== null,
                        },
                    ])}
                >
                    <FieldLabel className={styles.label}>
                        <FormattedMessage defaultMessage="Address" description="Field label" />
                    </FieldLabel>
                    <TextField
                        className={styles.input}
                        {...fields.address}
                        placeholder={intl.formatMessage({
                            defaultMessage: 'Address',
                            description: 'Field placeholder',
                        })}
                    />
                    {addressErrors !== null ? (
                        <div className={styles.errors}>{addressErrors.join(<br />)}</div>
                    ) : null}
                </div>

                <div
                    className={classNames([
                        styles.formGroup,
                        {
                            [styles.hasError]: cityErrors !== null,
                        },
                    ])}
                >
                    <FieldLabel className={styles.label}>
                        <FormattedMessage defaultMessage="City" description="Field label" />
                    </FieldLabel>
                    <TextField
                        className={styles.input}
                        {...fields.city}
                        placeholder={intl.formatMessage({
                            defaultMessage: 'City',
                            description: 'Field placeholder',
                        })}
                    />
                    {cityErrors !== null ? (
                        <div className={styles.errors}>{cityErrors.join(<br />)}</div>
                    ) : null}
                </div>

                <div
                    className={classNames([
                        styles.formGroup,
                        {
                            [styles.hasError]: postalCodeErrors !== null,
                        },
                    ])}
                >
                    <FieldLabel className={styles.label}>
                        <FormattedMessage defaultMessage="Postal code" description="Field label" />
                    </FieldLabel>
                    <TextField
                        className={styles.input}
                        {...fields.postal_code}
                        placeholder={intl.formatMessage({
                            defaultMessage: 'Postal code',
                            description: 'Field placeholder',
                        })}
                    />
                    {postalCodeErrors !== null ? (
                        <div className={styles.errors}>{postalCodeErrors.join(<br />)}</div>
                    ) : null}
                </div>

                <div
                    className={classNames([
                        styles.formGroup,
                        {
                            [styles.hasError]: regionErrors !== null,
                        },
                    ])}
                >
                    <FieldLabel className={styles.label}>
                        <FormattedMessage defaultMessage="Province" description="Field label" />
                    </FieldLabel>
                    <ProvinceField
                        className={classNames([styles.input, styles.select])}
                        placeholderClassName={classNames([
                            styles.selectPlaceholder,
                            {
                                [selectPlaceholderClassName]: selectPlaceholderClassName !== null,
                            },
                        ])}
                        {...fields.region}
                        // placeholder={intl.formatMessage({
                        //     defaultMessage: 'Region',
                        //     description: 'Field placeholder',
                        // })}
                    />
                    {regionErrors !== null ? (
                        <div className={styles.errors}>{regionErrors.join(<br />)}</div>
                    ) : null}
                </div>

                {prizeItemsOptions !== null ? (
                    <div
                        className={classNames([
                            styles.formGroup,
                            {
                                [styles.hasError]: prizeItemIdErrors !== null,
                            },
                        ])}
                    >
                        <SelectField
                            className={styles.input}
                            {...fields.prize_item_id}
                            items={prizeItemsOptions}
                            placeholder={
                                prizeType === 'ticket'
                                    ? intl.formatMessage({
                                          defaultMessage: 'Ticket date',
                                          description: 'Field placeholder',
                                      })
                                    : intl.formatMessage({
                                          defaultMessage: 'Prize',
                                          description: 'Field placeholder',
                                      })
                            }
                            placeholderClassName={classNames([
                                styles.selectPlaceholder,
                                {
                                    [selectPlaceholderClassName]:
                                        selectPlaceholderClassName !== null,
                                },
                            ])}
                        />
                        {prizeItemIdErrors !== null ? (
                            <div className={styles.errors}>{prizeItemIdErrors.join(<br />)}</div>
                        ) : null}
                    </div>
                ) : null}

                {(prizeFields || []).map(
                    (
                        { name, label, type, options, helpText = null, ...rest },
                        prizeFieldsIndex,
                    ) => {
                        const fieldErrors = (errors || {})[name] || null;
                        let field = null;
                        if (type === 'select') {
                            field = (
                                <SelectField
                                    {...fields[name]}
                                    {...rest}
                                    className={styles.input}
                                    items={(options || []).map(({ label: optionLabel, value }) => ({
                                        label: optionLabel,
                                        value,
                                    }))}
                                    placeholder={label}
                                    placeholderClassName={classNames([
                                        styles.selectPlaceholder,
                                        {
                                            [selectPlaceholderClassName]:
                                                selectPlaceholderClassName !== null,
                                        },
                                    ])}
                                />
                            );
                        } else if (type === 'date') {
                            field = (
                                <DateField
                                    {...fields[name]}
                                    {...rest}
                                    className={styles.date}
                                    inputClassName={styles.input}
                                    type={type}
                                />
                            );
                        } else {
                            field = (
                                <TextField
                                    {...fields[name]}
                                    {...rest}
                                    className={styles.input}
                                    type={type}
                                    placeholder={label}
                                />
                            );
                        }
                        return (
                            <div
                                key={`prizefields-${prizeFieldsIndex + 1}`}
                                className={classNames([
                                    styles.formGroup,
                                    {
                                        [styles.hasError]: fieldErrors !== null,
                                    },
                                ])}
                            >
                                {type === 'date' ? (
                                    <FieldLabel className={styles.label}>{label}</FieldLabel>
                                ) : null}
                                {field}
                                {helpText !== null ? (
                                    <div className={styles.helpText}>{helpText}</div>
                                ) : null}

                                {fieldErrors !== null ? (
                                    <div className={styles.errors}>{fieldErrors.join(<br />)}</div>
                                ) : null}
                            </div>
                        );
                    },
                )}
                <div
                    className={classNames([
                        styles.formGroup,
                        styles.math,
                        {
                            [styles.hasError]: mathErrors !== null,
                        },
                    ])}
                >
                    <FieldLabel className={styles.label}>
                        <FormattedMessage
                            defaultMessage="Winner’s skill testing question : (24x2) / 2 ="
                            description="Field label"
                        />
                    </FieldLabel>
                    <TextField
                        className={styles.input}
                        {...fields.math}
                        placeholder={intl.formatMessage({
                            defaultMessage: 'Your answer',
                            description: 'Field placeholder',
                        })}
                    />
                    {mathErrors !== null ? (
                        <div className={styles.errors}>{mathErrors.join(<br />)}</div>
                    ) : null}
                </div>
                <SubmitButton className={styles.submit} type="submit">
                    <FormattedMessage defaultMessage="Submit" description="Button label" />
                </SubmitButton>
            </div>
            <div className={styles.errors}>
                {generalError !== null ? <div>{generalError}</div> : null}
            </div>
        </form>
    );
}

PrizeForm.propTypes = propTypes;
PrizeForm.defaultProps = defaultProps;

export default PrizeForm;
